import { IContentElementMoveableDragDrop } from "src/app/ui-testrunner/element-render-moveable-dnd/model";
import { ElementType, IContentElement } from "../../ui-testrunner/models";
import { IContentElementGroup } from "src/app/ui-testrunner/element-render-grouping/model";
import { IContentElementInsertion } from "src/app/ui-testrunner/element-render-insertion/model";
import { IContentElementOrder, IContentElementOrderOption, OrderMode } from "src/app/ui-testrunner/element-render-order/model";
import { IContentElementDndDraggable } from "src/app/ui-testrunner/element-render-dnd/model";
import { IContentElementMcq, McqDisplay, mcqOptionLabels } from "src/app/ui-testrunner/element-render-mcq/model";
import { ESelectionTableScoreMode, IContentElementSelectionTable } from "src/app/ui-testrunner/element-render-selection-table/model";

// List which element types need a score matrix (which was implemented)
export const scoreMatrixElementTypes: (ElementType)[] = [
  ElementType.GROUPING,
  ElementType.MOVEABLE_DND,
  ElementType.INSERTION,
  ElementType.ORDER,
  ElementType.MCQ,
  ElementType.CUSTOM_MCQ,
  ElementType.SELECT_TABLE
]

export enum MatrixHeaderType {
  TEXT = "TEXT",
  IMAGE = "IMAGE",
  MATH = "MATH",
  TABLE = "TABLE",
}
export interface IMatrixHeader {
  type: MatrixHeaderType,
  content: string
}
export interface IMatrixColHeader extends IMatrixHeader {};
export interface IMatrixRowHeader extends IMatrixHeader {
  id?: string | number;
  key_id?: string|number;
  option: IContentElementDndDraggable | IContentElementOrderOption;
  // element-level settings that affect scoring, duplicated onto every row so
  // checkRowsOutdated (which only diffs row-level keys) notices when they change
  scoreWeight?: number;
  enableProportionalScoring?: boolean;
  scoreMode?: ESelectionTableScoreMode;
  maxCheckedPerRow?: number;
  maxCheckedPerCol?: number;
  isMaxDenoScoring?: boolean;
  isMustHaveOnePerRow?: boolean;
  isMustHaveOnePerCol?: boolean;
  isMultiSelect?: boolean;
  maxOptions?: number;
  isEnablePartialweights?: boolean;
  isOptional?: boolean;
}
export interface IMatrixValueMetadata{
  isTargetIDMissing?: boolean;
  isOptionIDMissing?: boolean;
  numberRequired?: number; // for grouping as sometimes an option needs to go into a target multiple times
  linkedCombinations?: any[]; // for DND as certain correct values are linked to combinations where that value is correct

}
export interface IMatrixValues {
  value: string | number
  metadata?: IMatrixValueMetadata;
}

export interface IScoreMatrix {
  columns: IMatrixColHeader[],
  rows: IMatrixRowHeader[],
  values: IMatrixValues[][],
  isConfirmed: boolean,
  isUpdated: boolean
}
/**
 * Transform an element into content to be rendered inside the row/column header cell
 * Can be reused by different `getMatrix...` functions
 */
export const elementToMatrixHeader = (element:IContentElement) : IMatrixHeader => {
  let type, content;
  switch(element?.elementType){
    case ElementType.TEXT:
      type = MatrixHeaderType.TEXT;
      content = element.caption;
      break;
    case ElementType.IMAGE:
      type = MatrixHeaderType.IMAGE;
      content = element?.images?.default?.image?.url ?? element?.url;
      break;
    case ElementType.MATH:
      type = MatrixHeaderType.MATH;
      content = element.latex || element.content;
      break;
    case ElementType.TABLE:
      type = MatrixHeaderType.TEXT;
      content =  `Table ${element.entryId? element.entryId:""}`;
      break;
    default:
      type = MatrixHeaderType.TEXT;
      content = ""
      break;
  }
  return {type, content}
}


/** Obtain the matrix rows (options) and columns (targets) for a block element */
export const getMatrix = (element: IContentElement): { rows: IMatrixRowHeader[]; columns: IMatrixColHeader[]; values: IMatrixValues[][] } => {
  let rows: IMatrixRowHeader[] = [];
  let columns: IMatrixColHeader[] = [];
  let values: IMatrixValues[][] = [];

  switch (element.elementType) {
    case ElementType.GROUPING:
      ({ rows, columns, values } = getMatrixGrouping(<IContentElementGroup>element));
      // values = getMatrixValuesGrouping(<IContentElementGroup>element, columns, rows);
      break;
    case ElementType.MOVEABLE_DND:
      ({ rows, columns, values } = getMatrixDND(<IContentElementMoveableDragDrop>element));
      break;
    case ElementType.INSERTION:
      ({ rows, columns, values } = getMatrixInsertion(<IContentElementInsertion>element));
      // values = getMatrixValuesInsertion(<IContentElementInsertion> element, columns, rows);
      break;
    case ElementType.ORDER:
      ({ rows, columns, values} = getMatrixOrder(<IContentElementOrder>element));
      break;
    case ElementType.MCQ:
    case ElementType.CUSTOM_MCQ:
      ({ rows, columns, values } = getMatrixMCQ(<IContentElementMcq>element));
      break;
    case ElementType.SELECT_TABLE:
      ({rows, columns, values } = getMatrixSelectTable(<IContentElementSelectionTable>element));
      break;
    default:
      console.error("Matrix Dimension Unavailable for element type " + element.elementType);
  }

  return { rows, columns, values };
};

const getMatrixGrouping = (element:IContentElementGroup | IContentElementMoveableDragDrop) : {rows: IMatrixRowHeader[], columns: IMatrixColHeader[], values: IMatrixValues[][]} => {
  const rows:IMatrixRowHeader[] = [];
  const columns:IMatrixColHeader[] = [];
  const values = [];
  // Targets as columns - Only use the ID of the target
  element.targets?.forEach(target => {
    const newColumn: IMatrixColHeader = {
      type: MatrixHeaderType.TEXT,
      content: ''+ (target.id ?? '')
    }
    columns.push(newColumn)
  })
  // Draggables as rows
  const areOptionsReusable = element.isOptionsReusable;
  element.draggables?.forEach(draggable => {
    let {type, content} = elementToMatrixHeader(draggable.element);
    const valuesForRow: IMatrixValues[] = [];
    const newRow: IMatrixRowHeader = {
      type,
      content,
      key_id: draggable.key_id ?? '',
      id: draggable.id ?? '',
      option: JSON.parse(JSON.stringify(draggable))
    }
    columns.forEach((col)=>{
      
      if(!areOptionsReusable){
        if (draggable.id && col.content){
          if("" + draggable.id === col.content){
            valuesForRow.push({value: 1});
          }
          else{
            valuesForRow.push({value: 0});
          }
        }
        else {
          valuesForRow.push({
            value: null,
            metadata: {
              isTargetIDMissing: col.content?false:true,
              isOptionIDMissing:  draggable.id?false:true
            }
          });
        }
      }
      else {
        if( draggable?.targetID2Amount?.[col.content] > 0){
          valuesForRow.push({
            value: 1,
            metadata: {
              numberRequired: +draggable.targetID2Amount[col.content],
            }
          });
        }
        else if (col.content) {
          valuesForRow.push({
            value: 0,
            metadata: {
              numberRequired: +draggable.targetID2Amount?.[col.content],
            }
          });
        } else {
          valuesForRow.push({
            value: null,
            metadata: {
              isTargetIDMissing: true
            }
          });
        }
      }
    
    });
    values.push(valuesForRow);
    rows.push(newRow);
  });
  return {rows, columns, values}
}

const getMatrixDND = (element: IContentElementMoveableDragDrop) : {rows: IMatrixRowHeader[], columns: IMatrixColHeader[], values: IMatrixValues[][]} => {
  const rows:IMatrixRowHeader[] = [];
  const columns:IMatrixColHeader[] = [];
  const values = [];
  const isMultipleOptionsRight = element.isMultipleOptionsRight;
  const isAcceptMultipleCombinations = element.isAcceptMultipleCombinations;
  // Targets as columns - Only use the ID of the target
  element.targets?.forEach(target => {
    const newColumn: IMatrixColHeader = {
      type: MatrixHeaderType.TEXT,
      content: ''+ (target.id ?? '')
    }
    columns.push(newColumn)
  })
  // Draggables as rows

  element.draggables?.forEach(draggable => {
    let {type, content} = elementToMatrixHeader(draggable.element);
    const valuesForRow: IMatrixValues[] = [];
    const newRow: IMatrixRowHeader = {
      type,
      content,
      id: draggable.id ?? '',
      option: JSON.parse(JSON.stringify(draggable))
    }
    columns.forEach((col, colIndex) => {
      if(isAcceptMultipleCombinations){
        const correctCombinationsIndex = [];
        element?.multipleCombinations?.forEach((combination, index)=>{
          if(combination[col.content] === draggable.id){
            correctCombinationsIndex.push(index);
          }
        });
        if(correctCombinationsIndex.length){
          valuesForRow.push({
            value: 1,
            metadata: {
              linkedCombinations: correctCombinationsIndex
            }
          });
        }
        else {
          valuesForRow.push({
            value: 0,
            metadata: {
              isTargetIDMissing: col.content?false:true,
              isOptionIDMissing:  draggable.id?false:true
            }
          })
        }

      }
      else if(!isMultipleOptionsRight){
        if (draggable.id && col.content){
          if(draggable.targetId + "" === col.content){
            valuesForRow.push({value: 1});
          }
          else{
            valuesForRow.push({value: 0});
          }
        }
        else {
          valuesForRow.push({
            value: null,
            metadata: {
              isTargetIDMissing: col.content?false:true,
              isOptionIDMissing:  draggable.id?false:true
            }
          });
        }
      }
      else {
        const isCorrect = element.pairMapping?.filter((pair) => pair.optionID+"" === draggable.id+"" && pair.targetID === col.content);
        if(isCorrect?.length){
          valuesForRow.push({value: 1});
        } 
        else if(col.content) {
          valuesForRow.push({value: 0})
        }
        else {
          valuesForRow.push({
            value:null, 
            metadata: {
              isTargetIDMissing: true
            }
          })
        }
      }
    })
    values.push(valuesForRow);
    rows.push(newRow);
  })
  return {rows, columns, values}
}

const getMatrixInsertion = (element: IContentElementInsertion) : {rows: IMatrixRowHeader[], columns: IMatrixColHeader[], values: IMatrixValues[][]} => {
  const rows:IMatrixRowHeader[] = [];
  const columns:IMatrixColHeader[] = [];
  let values: IMatrixValues[][] = [];

  element.textBlocks?.forEach((textBlock) => {
    if (textBlock.element.elementType === ElementType.BLANK || textBlock.element.elementType === ElementType.BLANK_DEPRECIATED) {
      columns.push({
        type: MatrixHeaderType.TEXT,
        content: (textBlock.id ?? '').toString(),
      });
    }
  });

  element.draggables?.forEach(draggable => {
    let {type, content} = elementToMatrixHeader(draggable.element);
    const valuesForRow: IMatrixValues[] = []
    columns.forEach((col, colIndex) => {
      if (draggable.id && col.content){
        if(""+draggable.id === col.content){
          valuesForRow.push({value: 1});
        }
        else{
          valuesForRow.push({value: 0});
        }
      }
      else {
        valuesForRow.push({
          value: null,
          metadata: {
            isTargetIDMissing: col.content?false:true,
            isOptionIDMissing:  draggable.id?false:true
          }
        });
      }
    })
    values.push(valuesForRow)
    const newRow: IMatrixRowHeader = {
      type,
      content,
      key_id: draggable.key_id ?? '',
      id: draggable.id ?? '',
      option: JSON.parse(JSON.stringify(draggable))
    }
    rows.push(newRow)
  })
  return {rows, columns, values}
}

const getMatrixOrder = (element: IContentElementOrder) : {rows: IMatrixRowHeader[], columns: IMatrixColHeader[], values: IMatrixValues[][]} => {
  const rows:IMatrixRowHeader[] = [];
  const columns:IMatrixColHeader[] = [];
  const values: IMatrixValues[][] = [];
  const rowItemsToProcesss = element.orderMode === OrderMode.TARGET ? element.options.filter(option => !option.isReadOnly): element.options;
  
  var count = 1;
  element.options?.forEach((option) => {//? I'm not sure how ordering mode calculates what is wrong and what is right and what exactly targets would be in this context
    if(!option.isReadOnly || element.orderMode === OrderMode.REORDER){// this is due to the fact that we can't count fixed elements in target mode but in reorder they still appear
      columns.push({
        type: MatrixHeaderType.TEXT,
        content: ""+count++,
      });
    }
  });
  
  rowItemsToProcesss?.forEach((item, index) => {
    const { type, content } = elementToMatrixHeader(item.element || item);
    const newRow: IMatrixRowHeader = {
      type,
      content,
      key_id: item.key_id ?? '',
      option: JSON.parse(JSON.stringify(item))
    };
    const valuesForRow: IMatrixValues[] = []
    columns.forEach((col, colIndex) => {
      if(col.content === (index+1).toString()){
        valuesForRow.push({value: 1});
      }
      else{
        valuesForRow.push({value: 0});
      }
    })
    values.push(valuesForRow)
    rows.push(newRow);
  });
  return {rows, columns, values}
}

const getMatrixMCQ = (element: IContentElementMcq) : {rows: IMatrixRowHeader[], columns: IMatrixColHeader[], values: IMatrixValues[][]} => {

  const maxSelectable = element.isMultiSelect && ![McqDisplay.CUSTOM_DROPDOWN, McqDisplay.DROPDOWN].includes(element.displayStyle)
  ? element.maxOptions || element.options?.length  // if multi select is on and it's not drop down
  : element.options?.length ? 1 : 0; // else use 1 if there are options

  const colTitle = `Max: ${maxSelectable}`
  const rows:IMatrixRowHeader[] = [];
  const columns:IMatrixColHeader[] = [{type: MatrixHeaderType.TEXT, content: colTitle}];
  const values: IMatrixValues[][] = [];
  let optionCount = 0;
  element.options?.forEach(option =>{
    if(!option) return; // a blank option slot must not break matrix generation (and with it saving the item)
    const valuesForRow: IMatrixValues[] = []
    if(option.isCorrect){
      valuesForRow.push({value: 1});
    }else{
      valuesForRow.push({value: 0});
    }
    const {type, content} = elementToMatrixHeader(option);
    rows.push({
      type,
      content,
      key_id: mcqOptionLabels[optionCount++],
      option: JSON.parse(JSON.stringify(option)),
      isMultiSelect: element.isMultiSelect ?? false,
      scoreWeight: element.scoreWeight,
      maxOptions: element.maxOptions,
      enableProportionalScoring: element.enableProportionalScoring ?? false,
      isEnablePartialweights: element.isEnablePartialweights ?? false,
      isOptional: element.isOptional ?? false,
    })
    values.push(valuesForRow)
  });
  return {rows, columns, values};
}
const getMatrixSelectTable = (element: IContentElementSelectionTable) : {rows: IMatrixRowHeader[], columns: IMatrixColHeader[], values: IMatrixValues[][]} => {
  const rows:IMatrixRowHeader[] = [];
  const columns:IMatrixColHeader[] = [];
  const values: IMatrixValues[][] = [];
  element.topRow?.forEach(col =>{ // top row is the header for the cols
    const {type, content} = elementToMatrixHeader(col.content);
    columns.push({
      type,
      content
    });
  });

  element.leftCol?.forEach(row =>{ // left col is the header for the rows
    const {type, content} = elementToMatrixHeader(row.content);
    rows.push({
      type,
      content,
      option: <any>{},
      // scoring is also driven by these element-level settings, not just the checked cells
      scoreWeight: element.scoreWeight,
      enableProportionalScoring: element.enableProportionalScoring ?? false,
      scoreMode: element.scoreMode ?? ESelectionTableScoreMode.LEGACY,
      maxCheckedPerRow: element.maxCheckedPerRow,
      maxCheckedPerCol: element.maxCheckedPerCol,
      isMaxDenoScoring: element.isMaxDenoScoring ?? false,
      isMustHaveOnePerRow: element.isMustHaveOnePerRow ?? false,
      isMustHaveOnePerCol: element.isMustHaveOnePerCol ?? false,
    });
  });
  
  element.checkBoxRows?.forEach(row =>{
    const valuesForRow: IMatrixValues[] = []
    row?.forEach(cell =>{
      if(cell.value){
        valuesForRow.push({value: 1});
      }else{
        valuesForRow.push({value: 0});
      }
    });
    values.push(valuesForRow);
  })

  return {rows, columns, values};
}


/**
 * Refresh the score matrix based on the content of the element
 * For any unchanged rows or columns, preserve the old values - otherwise use blank values
 * @param element The block element which contains the matrix (is updated in-place)
 */
export const refreshScoreMatrix = (element:IContentElement, forceRefresh = false) => {

  // Element must qualify to have a matrix, otherwise skip
  if (!scoreMatrixElementTypes.includes(element.elementType as ElementType)) {
    return
  }

  // If no matrix exists on the block yet, make a blank one
  if (!element.scoreMatrix){
    element.scoreMatrix = {
      isUpdated: false,
      isConfirmed: false,
      rows: [],
      columns: [],
      values: []
    }
  }
  if(element.scoreMatrix.isConfirmed === undefined){
    element.scoreMatrix.isConfirmed = false;
  }
  if(element.scoreMatrix.isUpdated === undefined){
    element.scoreMatrix.isUpdated = false;
  }

  // Get the previous matrix content
  const prevColumns = element.scoreMatrix.columns
  const prevRows = element.scoreMatrix.rows
  const prevValues = element.scoreMatrix.values

  // Generate matrix columns and rows based on the element
  const {columns, rows, values} = getMatrix(element)
  // compare as saved: NaN becomes null and undefined is dropped when the matrix is stored, so a raw compare would flag it outdated forever
  const rowsOutdated = checkRowsOutdated(prevRows, JSON.parse(JSON.stringify(rows)), element.elementType);
  if((JSON.stringify(prevColumns) !== JSON.stringify(columns) || rowsOutdated || JSON.stringify(prevValues) !== JSON.stringify(values))&& element.scoreMatrix.isConfirmed){
    element.scoreMatrix.isUpdated = false;
  }
  else {
    element.scoreMatrix.isUpdated = true;
  }
  // Update to new matrix
  if(!element.scoreMatrix.isConfirmed || forceRefresh){
    element.scoreMatrix.rows = rows;
    element.scoreMatrix.columns = columns;
    element.scoreMatrix.values = values;
  }
}

/**
 * Get the score matrix the block would have after a refresh, without modifying the block.
 * The stored isUpdated flag is only recalculated when the block is opened in the editor or the item is saved,
 * so anything reading the matrix status of unopened items (e.g. audits) should use this instead.
 * @param element The block element which contains the matrix
 */
export const getRefreshedScoreMatrix = (element:IContentElement): IScoreMatrix => {
  if (!scoreMatrixElementTypes.includes(element.elementType as ElementType)) {
    return element.scoreMatrix
  }
  const elementCopy = {...element, scoreMatrix: element.scoreMatrix ? {...element.scoreMatrix} : undefined}
  refreshScoreMatrix(elementCopy)
  return elementCopy.scoreMatrix
}

const checkRowsOutdated = (prevRows:IMatrixRowHeader[], rows:IMatrixRowHeader[], elementType:ElementType | string): boolean => {
  let rowsOutdated = false;
  if(prevRows?.length !== rows?.length){
    rowsOutdated = true;
  } else {
    for(let idx = 0; idx < rows.length && !rowsOutdated; idx++){
      const current = rows[idx];
      const previous = prevRows[idx];
      // undefined settings are dropped when the matrix is saved, so they must not count as a row change
      const currKeys = Object.keys(current).filter(key => current[key] !== undefined);
      const prevKeys = Object.keys(previous).filter(key => previous[key] !== undefined);
      if(prevKeys.length !== currKeys.length){
        rowsOutdated = true;
        break;
      }
      for(let key of currKeys){
        if(key === 'option'){
          // only check relevant keys in options that affect scoring
          const keysToCheck = getScoringOptionKeys(elementType);
          for(let commonKey of keysToCheck){
            if(
              previous[key]?.[commonKey] !== current[key]?.[commonKey] && // if they aren't equal
              !(!previous[key]?.[commonKey] && !current[key]?.[commonKey]) // and they aren't both falsely
            ){
              rowsOutdated= true;
              break
            }
          }
        } else if(previous[key] !== current[key]){
          rowsOutdated = true;
          break;
        }
      }
      
    }
  }
  return rowsOutdated;
}

const COMMON_OPTION_KEY_CHECKS = ['key_id', 'id']
const OPTION_KEY_CHECKS_MAP = {
  'moveable_dnd' : ['targetId', ],
  'mcq': ['partialWeight'],
  'custom_mcq': ['partialWeight'],
}

/** Option keys that affect scoring for a matrix row's `option` */
export const getScoringOptionKeys = (elementType: ElementType | string): string[] => {
  return COMMON_OPTION_KEY_CHECKS.concat(OPTION_KEY_CHECKS_MAP[elementType] ?? []);
}